/* eslint-disable no-restricted-imports */
import React, { useState, useRef } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useDismiss,
  useRole,
  useMergeRefs,
  useInteractions,
  FloatingPortal,
  useClick,
  useListNavigation,
  useListItem,
  FloatingFocusManager,
  FloatingList,
  useTransitionStatus,
} from '@floating-ui/react';

import { useFloatingPortalTarget } from '../hooks/useFloatingPortalTarget';
import { CheckIcon } from '@phosphor-icons/react';
import classNames from 'classnames';

const DIALOG_PANEL_OFFSET = 8;
const DIALOG_WINDOW_EDGE_PADDING = 8; // The space in px a panel is padded from the edge of the window

type ContextType = {
  setOpen: (state) => void;
  activeIndex: number;
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'] | Function;
} | null;

const RCMenuContext = React.createContext<ContextType>({
  setOpen: () => {},
  activeIndex: undefined,
  getItemProps: () => {},
});

const useRCMenuContext = () => {
  const context = React.useContext(RCMenuContext);

  return context;
};

interface MenuOptions {
  /** The element to which this menu will anchor. */
  anchor: JSX.Element;

  /** Optionally control the state of the menu. */
  isOpen?: boolean;

  /** Open / Close event handler. */
  onOpenChange?: (open: boolean) => void;

  /** The menu contents should be wrapped by this component. Contents should be comprised of RCMenu.Item and RCMenu.Separator subcomponents. */
  children: React.ReactNode;

  /** Initial menu placement. Defaults to 'bottom-start' (left aligned). */
  position?: 'bottom-start' | 'bottom-end';
}

/**
 * RCMenu provides a standardized menu that attaches to a toggling component & provides animations, keyboard support, and other niceties out of the box.
 */
export function RCMenu({
  anchor,
  position = 'bottom-start',
  isOpen: openProp = undefined,
  onOpenChange: setOpenProp = undefined,
  children,
}: React.PropsWithChildren<MenuOptions>) {
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [openState, setOpenState] = useState<boolean>(openProp || false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const portalTarget = useFloatingPortalTarget();

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  const {
    x,
    y,
    refs,
    strategy,
    placement,
    context,
  } = useFloating({
    placement: position,
    open: open,
    onOpenChange: setOpen,
    middleware: [
      offset(DIALOG_PANEL_OFFSET),
      flip({
        mainAxis: true,
        crossAxis: 'alignment',
        fallbackAxisSideDirection: 'start',
      }),
      shift({
        crossAxis: true,
        padding: {
          top: DIALOG_WINDOW_EDGE_PADDING,
          right: DIALOG_WINDOW_EDGE_PADDING,
          bottom: DIALOG_WINDOW_EDGE_PADDING,
          left: DIALOG_WINDOW_EDGE_PADDING,
        },
      }),
    ],
    whileElementsMounted: (r, f, u) => {
      // We only need to auto update if it's possible to do so. This check also ensures
      // functionality in test environments (jsdom) that lack ResizeObserver.
      if (('ResizeObserver' in window)) {
        return autoUpdate(r, f, u);
      }
      return;
    },
  });

  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  } = useInteractions([
    useRole(context, {
      role: 'menu',
    }),
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePressEvent: 'mousedown',
      outsidePress: true,
      referencePress: false,
    }),
    useClick(context, {
      enabled: true,
      toggle: true,
    }),
    useListNavigation(context, {
      listRef: listRef,
      activeIndex,
      onNavigate: setActiveIndex,
      virtual: false,
      disabledIndices: [],
    }),
  ]);

  const { isMounted, status } = useTransitionStatus(context);
  const anchorRef = useMergeRefs([refs.setReference, (anchor as any).ref]);

  if (anchor.type === React.Fragment) {
    // Fragments will render to the screen, but they lose their ref and the Popover UIs won't know what to anchor
    // themselves to, and the panel will end up in the top-left corner of the screen.
    throw new Error('Pass a single element to the `anchor` prop, Fragments or other arrays are not supported.');
  }

  return (
    <RCMenuContext.Provider value={{ setOpen, activeIndex, getItemProps }}>
      {React.cloneElement(anchor, {
        ...getReferenceProps({ ref: anchorRef, ...anchor.props }),
      })}

      {isMounted && (
        <FloatingPortal {...portalTarget}>
          <FloatingFocusManager
            context={context}
            modal={false}
            returnFocus={true}
          >
            <div
              ref={refs.setFloating}
              className="rc-menu__panel"
              data-status={status}
              data-placement={placement}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              }}
              {...getFloatingProps()}
            >
              <ul className="rc-menu__items" role="none">
                <FloatingList elementsRef={listRef}>
                  {children}
                </FloatingList>
              </ul>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </RCMenuContext.Provider>
  );
}

type RCMenuItemProps = SelectableMenuItem | MenuItem;

interface BaseProps {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  preventCloseOnClick?: boolean;
  children?: React.ReactNode;
}
interface SelectableMenuItem extends BaseProps {
  itemRole?: 'checkbox' | 'radio';
  selected?: boolean;
  icon?: never;
  destructive?: never;
  customElement?: never;
}

interface MenuItem extends BaseProps {
  itemRole?: 'default',
  destructive?: boolean;
  icon?: React.ReactElement;
  customElement?: (passthroughProps: PassthroughProps) => React.ReactElement;
  selected?: never;
}

interface PassthroughProps {
  ref?: React.ForwardedRef<any>;
  role?: HTMLElement['role'];
  'aria-checked'?: boolean;
  disabled?: undefined;
  destructive?: boolean;
  'aria-disabled'?: boolean;
  tabIndex?: number;
  onClick?: React.MouseEventHandler<HTMLElement>;
  icon?: React.ReactElement;
}

const RCMenuItem = React.forwardRef<HTMLElement, RCMenuItemProps>(
  function RCMenuItem({
    children,
    itemRole = 'default',
    selected = false,
    disabled = false,
    destructive = false,
    onClick: passthroughOnClick = undefined,
    preventCloseOnClick = false,
    icon = undefined,
    customElement = undefined,
  }: RCMenuItemProps, propRef) {
    const roleMap = {
      'default': 'menuitem',
      'checkbox': 'menuitemcheckbox',
      'radio': 'menuitemradio',
    };
    const { setOpen, activeIndex, getItemProps } = useRCMenuContext();
    const { ref, index } = useListItem();

    const itemRef = useMergeRefs([propRef, ref]);

    function handleClick() {
      if (preventCloseOnClick) return;
      setOpen(false);
    }

    const passthroughProps = {
      ref: itemRef,
      role: roleMap[itemRole],
      'aria-checked': itemRole === 'default' ? undefined : selected,
      disabled: undefined,
      'data-destructive': destructive ? destructive : undefined,
      'aria-disabled': disabled,
      tabIndex: activeIndex === index ? 0 : -1,
      ...getItemProps({
        onClick: (e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          passthroughOnClick?.(e);
          handleClick();
        },
      }),
    };

    return (
      <li className={classNames(
        'rc-menu__item',
        { 'rc-menu__item--destructive': destructive },
        { 'rc-menu__item--disabled': disabled },
        { 'rc-menu__item--with-icon': icon },
      )} role="none">
        {(selected || icon) &&
          <div className="rc-menu__item__icon" aria-hidden="true">
            {selected ? <CheckIcon weight="bold" /> : icon}
          </div>
        }
        {customElement ? (
          customElement(passthroughProps)
        ) : (
          <button
            type="button"
            {...passthroughProps}
          >
            {children}
          </button>
        )}
      </li>
    );
  },
);

function RCMenuGroup({
  label = undefined,
  children,
}: React.PropsWithChildren<{ label?: string }>) {
  return (
    <li className="rc-menu__group" role="none">
      {label &&
        <span className="rc-menu__group-label" aria-hidden="true">{label}</span>
      }
      <ul className="rc-menu__group__items" role="group" aria-label={label}>
        {children}
      </ul>
    </li>
  );
}


function RCMenuSeparator() {
  return (
    <div role="separator" className="rc-menu__separator" />
  );
}

RCMenu.Item = RCMenuItem;
RCMenu.Separator = RCMenuSeparator;
RCMenu.Group = RCMenuGroup;
